import React, { useState, useEffect, useContext } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import { useTheme } from "./ThemeContext";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import { DeviceContext } from "../context/DeviceContext";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Utils from "../utils/Utils";

// Format date relative to now
const formatRelativeDate = (dateString) => {
  if (!dateString) return "-";
  // Backend LocalDateTime values arrive without a timezone designator but
  // are actually UTC. Normalize so they aren't parsed as local time.
  const date = new Date(Utils.ensureUtc(dateString));
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

// Truncate text
const truncateText = (text, maxLength) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

export default function CommentsHistoryTable({ userId }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { theme } = useTheme();
  const { isMobile } = useContext(DeviceContext);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchComments = async () => {
      if (!userId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/comments/user/${userId}`
        );
        setComments(response.data || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching comments:", err);
        setError("Failed to load comments");
        setComments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [userId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleCommentClick = (comment) => {
    navigate(`/community/${comment.communityName}/${comment.postId}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
        <CircularProgress sx={{ color: theme.palette.primary.main }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper
        sx={{
          width: "100%",
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: "1rem",
        }}
      >
        <Typography variant="body2" color="error" align="center">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (!comments || comments.length === 0) {
    return (
      <Paper
        sx={{
          width: "100%",
          p: 3,
          backgroundColor: theme.palette.background.paper,
          borderRadius: "1rem",
        }}
      >
        <Typography variant="body2" color="textSecondary" align="center">
          No comments yet.
        </Typography>
      </Paper>
    );
  }

  const paginatedComments = comments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Mobile Card View
  const renderMobileView = () => {
    return paginatedComments.map((comment, index) => (
      <Card
        key={comment.commentId || index}
        onClick={() => handleCommentClick(comment)}
        sx={{
          marginBottom: "0.5rem",
          backgroundColor: theme.palette.background.paper,
          cursor: "pointer",
          "&:hover": {
            backgroundColor: theme.palette.background.gray,
          },
        }}
      >
        <CardContent sx={{ padding: "0.75rem !important" }}>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              mb: 0.5,
              display: "block",
            }}
          >
            on{" "}
            <Typography
              component="span"
              variant="caption"
              sx={{ color: theme.palette.primary.main }}
            >
              {truncateText(comment.postTitle, 50)}
            </Typography>
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.primary,
              mb: 1,
              lineHeight: 1.4,
            }}
          >
            {truncateText(comment.content, 120)}
          </Typography>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ThumbUpIcon sx={{ fontSize: 14, color: theme.palette.text.green }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {comment.upvotes || 0}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <ThumbDownIcon sx={{ fontSize: 14, color: theme.palette.text.disabled }} />
                  <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                    {comment.downvotes || 0}
                  </Typography>
                </Box>
              </Stack>
            </Grid>
            <Grid item>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {formatRelativeDate(comment.createdAt)}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    ));
  };

  // Desktop Table View
  const renderDesktopView = () => {
    return (
      <>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="comments table" size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.background.gray,
                    fontWeight: "bold",
                    color: theme.palette.text.green,
                    padding: "0.6rem",
                    minWidth: "8rem",
                  }}
                >
                  Post
                </TableCell>
                <TableCell
                  sx={{
                    backgroundColor: theme.palette.background.gray,
                    fontWeight: "bold",
                    color: theme.palette.text.green,
                    padding: "0.6rem",
                  }}
                >
                  Comment
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    backgroundColor: theme.palette.background.gray,
                    fontWeight: "bold",
                    color: theme.palette.text.green,
                    padding: "0.6rem",
                    minWidth: "4rem",
                  }}
                >
                  Votes
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    backgroundColor: theme.palette.background.gray,
                    fontWeight: "bold",
                    color: theme.palette.text.green,
                    padding: "0.6rem",
                    minWidth: "5rem",
                  }}
                >
                  Posted
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedComments.map((comment) => {
                const netVotes = (comment.upvotes || 0) - (comment.downvotes || 0);
                return (
                  <TableRow
                    hover
                    key={comment.commentId}
                    onClick={() => handleCommentClick(comment)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell
                      sx={{
                        padding: "0.6rem",
                        color: theme.palette.text.secondary,
                        maxWidth: "150px",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: "0.8rem",
                        }}
                      >
                        {truncateText(comment.postTitle, 40)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        c/{comment.communityName}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "0.6rem",
                        color: theme.palette.text.primary,
                        maxWidth: "300px",
                      }}
                    >
                      {truncateText(comment.content, 80)}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        padding: "0.6rem",
                        color: netVotes >= 0 ? theme.palette.text.green : theme.palette.error.main,
                      }}
                    >
                      {netVotes >= 0 ? "+" : ""}{netVotes}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        padding: "0.6rem",
                        color: theme.palette.text.secondary,
                      }}
                    >
                      {formatRelativeDate(comment.createdAt)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={comments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: theme.palette.text.green,
            backgroundColor: theme.palette.background.gray,
          }}
        />
      </>
    );
  };

  return (
    <Paper
      sx={{
        width: "100%",
        overflow: "hidden",
        backgroundColor: theme.palette.background.paper,
        borderRadius: "1rem",
      }}
    >
      {isMobile ? renderMobileView() : renderDesktopView()}
      {isMobile && comments.length > rowsPerPage && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={comments.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: theme.palette.text.green,
            backgroundColor: theme.palette.background.gray,
          }}
        />
      )}
    </Paper>
  );
}
